import { ListItem, ListMeta, ListsPayload } from "./types";

// Lists get their own IndexedDB database rather than sharing the inventory queue in
// src/utils/OfflineQueue.ts. That queue's PendingItem shape is item-specific (one payload
// type plus a thumbnail data URL), and widening it would mean touching the inventory
// drain. Two databases also means neither drain can ever pick up the other's rows.
const DB_NAME = "WheresOurListsOffline";
const DB_VERSION = 1;
const OPS_STORE = "pendingOps";
const CACHE_STORE = "listsCache";

// Every mutation the context can make, in a form that survives a reload.
export type PendingListOp =
  | { kind: "list.upsert"; list: ListMeta }
  | { kind: "list.delete"; listId: string }
  | { kind: "item.upsert"; item: ListItem }
  | { kind: "item.delete"; listId: string; itemId: string }
  | { kind: "items.delete"; listId: string; itemIds: string[] };

export interface PendingListEntry {
  opId: string;
  op: PendingListOp;
  dataUserId: string;
  createdAt: number;
  attempts: number;
}

interface CachedLists extends ListsPayload {
  dataUserId: string;
  savedAt: number;
}

// Stable per target, so the same item edited five times offline collapses to one row and
// the last edit wins. Identical to how the inventory queue keys on ItemId.
export function opIdFor(op: PendingListOp): string {
  switch (op.kind) {
    case "list.upsert":
      return `list:${op.list.ListId}`;
    case "list.delete":
      return `list-delete:${op.listId}`;
    case "item.upsert":
      return `item:${op.item.ItemId}`;
    case "item.delete":
      return `item-delete:${op.itemId}`;
    case "items.delete":
      // Keyed by the set itself, so re-queuing the same bulk delete collapses too.
      return `items-delete:${[...op.itemIds].sort().join(",")}`;
  }
}

function targetsList(op: PendingListOp, listId: string): boolean {
  switch (op.kind) {
    case "list.upsert":
      return op.list.ListId === listId;
    case "item.upsert":
      return op.item.ListId === listId;
    case "list.delete":
    case "item.delete":
    case "items.delete":
      return op.listId === listId;
  }
}

// Queued work a newly queued op makes pointless. Deleting an item drops its pending
// edit; deleting a list drops everything queued for that list. The delete itself is
// still queued, because the row may already exist on the server, and DeleteItem on a
// row that was never created is a harmless no-op.
function supersededBy(op: PendingListOp, existing: PendingListEntry[]): string[] {
  if (op.kind === "item.delete") {
    const upsertId = `item:${op.itemId}`;
    return existing.filter((entry) => entry.opId === upsertId).map((e) => e.opId);
  }
  if (op.kind === "list.delete") {
    return existing
      .filter((entry) => targetsList(entry.op, op.listId))
      .map((entry) => entry.opId);
  }
  return [];
}

function openDB(): Promise<IDBDatabase> {
  return new Promise((resolve, reject) => {
    const request = indexedDB.open(DB_NAME, DB_VERSION);
    request.onupgradeneeded = (event) => {
      const db = (event.target as IDBOpenDBRequest).result;
      if (!db.objectStoreNames.contains(OPS_STORE)) {
        const store = db.createObjectStore(OPS_STORE, { keyPath: "opId" });
        store.createIndex("createdAt", "createdAt", { unique: false });
      }
      if (!db.objectStoreNames.contains(CACHE_STORE)) {
        db.createObjectStore(CACHE_STORE, { keyPath: "dataUserId" });
      }
    };
    request.onsuccess = () => resolve(request.result);
    request.onerror = () => reject(request.error);
  });
}

export async function queueOps(
  ops: PendingListOp[],
  dataUserId: string
): Promise<void> {
  if (ops.length === 0) {
    return;
  }
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OPS_STORE, "readwrite");
    const store = tx.objectStore(OPS_STORE);
    const readAll = store.getAll();

    readAll.onsuccess = () => {
      const existing = readAll.result as PendingListEntry[];
      const now = Date.now();
      ops.forEach((op, index) => {
        supersededBy(op, existing).forEach((opId) => store.delete(opId));
        const opId = opIdFor(op);
        // Re-queuing keeps the original timestamp so drain order stays stable and a
        // list created offline still syncs before the items added to it.
        const prior = existing.find((entry) => entry.opId === opId);
        store.put({
          opId,
          op,
          dataUserId,
          createdAt: prior?.createdAt ?? now + index,
          attempts: 0,
        });
      });
    };

    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

// Oldest first, via the createdAt index.
export async function queueGetAll(): Promise<PendingListEntry[]> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OPS_STORE, "readonly");
    const request = tx.objectStore(OPS_STORE).index("createdAt").getAll();
    request.onsuccess = () => {
      db.close();
      resolve(request.result as PendingListEntry[]);
    };
    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}

export async function queuePutEntry(entry: PendingListEntry): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OPS_STORE, "readwrite");
    tx.objectStore(OPS_STORE).put(entry);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

export async function queueDeleteOp(opId: string): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OPS_STORE, "readwrite");
    tx.objectStore(OPS_STORE).delete(opId);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

export async function queueCount(): Promise<number> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(OPS_STORE, "readonly");
    const request = tx.objectStore(OPS_STORE).count();
    request.onsuccess = () => {
      db.close();
      resolve(request.result);
    };
    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}

// The last known good copy of the page, so opening /Lists with no signal shows the list
// rather than an error. Written on every state change, so it already contains whatever
// edits are still sitting in the queue.
export async function cacheWrite(
  dataUserId: string,
  payload: ListsPayload
): Promise<void> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CACHE_STORE, "readwrite");
    const record: CachedLists = { ...payload, dataUserId, savedAt: Date.now() };
    tx.objectStore(CACHE_STORE).put(record);
    tx.oncomplete = () => {
      db.close();
      resolve();
    };
    tx.onerror = () => {
      db.close();
      reject(tx.error);
    };
  });
}

export async function cacheRead(
  dataUserId: string
): Promise<ListsPayload | null> {
  const db = await openDB();
  return new Promise((resolve, reject) => {
    const tx = db.transaction(CACHE_STORE, "readonly");
    const request = tx.objectStore(CACHE_STORE).get(dataUserId);
    request.onsuccess = () => {
      db.close();
      const record = request.result as CachedLists | undefined;
      resolve(
        record == null ? null : { lists: record.lists, items: record.items }
      );
    };
    request.onerror = () => {
      db.close();
      reject(request.error);
    };
  });
}
